// homeBuild.js — THE HOME'S BUILD TAB IS THE APP BUILDER (Nika 2026-10-23:
// "build tab should reuse ui/ux of /add-apps").
//
// The Build tab does not draw a builder of its own: it mounts the /add-app
// page itself (pages/add-app.js — the build conversation on the left, the
// build canvas with the ready-made apps, the person's apps and the built app
// on the right) in the home's grid area, through the route registry the
// PageSlot mounts routes from (`context.pages`, a lazy entry: the page's chunk
// loads on the first visit, the page skeleton shows meanwhile). One page, two
// places — every change to the builder reaches both.
//
// Three overrides make it a part of the home, not a second page in it: a
// `div`, no `main-content` id and no metadata (the home keeps its landmark,
// its skip link and its title), a clear ground (the home's own shows round
// the builder's sheet). And `scope.addAppThreadInUrl: false`: the home's URL
// is never the thread's link — `?thread=` is also mail's and chat's name
// (functions/assistantThreadUrl.js), so the builder neither reads nor clears
// it here.
//
//   homeBuildShown   the gate: the home's tabs on screen, Build the tab, no
//                    conversation holding the home
//   homeBuildMount   once, on the first show: the page into the view
//   homeBuildFit     the view's band — from its own top to the viewport's
//                    bottom (`--home-build-top`, components/HomeBuildView.js)
//   homeBuildPrefetch the builder's chunk loads while the pointer rests on the
//                    Build tab (the shell's route prefetch), so the tab opens
//                    on the builder, not on its skeleton
//
// ONE BUILDER AT A TIME. The builder's state is the root's (`_addApp*`, the
// `_ax_*` thread) and the AI's renders go to ONE address
// (`window.__addAppHost`), so two builders on screen would fight over both.
// On the home /add-app opens as this tab (functions/searchApps.js
// openAppPage, functions/forceBuildApp.js watchAiBuildOutput), and when a
// pane over the home holds /add-app anyway (a split, a pane that was already
// open) the home's builder leaves the tree (homeBuildUnmount) and comes back
// on the tab's next show:
//   homeBuildInPane   a pane over the home holds /add-app
//   homeBuildUnmount  the page out of the view, the skeleton back
//
// FRANK: every export is self-contained; siblings through `this.call`.

// The gate. Build is the tab on screen (homeViewOf: Apps mode, no Fly canvas,
// no deck), the tabs are (the home, no pane over it), and no conversation holds
// the home (a thread opened from the sidebar in Apps mode does). Every key is
// read up front (reactive callers).
export const homeBuildShown = function homeBuildShown(root) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this.context && this.context.functions) || {}
  const onScreen =
    typeof fns.homeTabsOnScreen === 'function' ? !!this.call('homeTabsOnScreen', r) : false
  const tab = typeof fns.homeViewOf === 'function' ? this.call('homeViewOf', r) : null
  const thread =
    typeof fns.assistantMainThreadOpen === 'function'
      ? !!this.call('assistantMainThreadOpen', r)
      : false
  return onScreen && tab === 'build' && !thread
}

// The /add-app page into the view, once. The route's entry is a lazy page
// (pages/lazyPage.js): its `scope.prefetch(scope)` resolves the page itself —
// the same load the shell's prefetch starts (functions/slotPrefetch.js). An
// eager entry is the page. A view gone before the chunk lands sets nothing; a
// load that fails leaves the skeleton and lets the next show try again.
export const homeBuildMount = async function homeBuildMount(el) {
  const host = el || this
  if (!host || host.__homeBuildMounted || typeof host.set !== 'function') return false
  const pages = (host.context && host.context.pages) || {}
  const entry = pages['/add-app']
  if (!entry || typeof entry !== 'object') return false
  host.__homeBuildMounted = true
  // an unmount while the chunk loads makes this mount stale (homeBuildUnmount)
  const seq = (host.__homeBuildSeq = (Number(host.__homeBuildSeq) || 0) + 1)
  let page = entry
  const scope = entry.scope
  if (scope && typeof scope.prefetch === 'function') {
    try {
      page = await scope.prefetch(scope)
    } catch (err) {
      if (host.__homeBuildSeq === seq) host.__homeBuildMounted = false
      console.warn('[homeBuild] the builder did not load:', err && err.message)
      return false
    }
  }
  // unmounted, gone, or a pane took the builder while the chunk loaded
  if (host.__homeBuildSeq !== seq) return false
  const root = host.state && host.state.root
  if (!page || typeof page !== 'object' || !host.__ref || this.call('homeBuildInPane', root)) {
    host.__homeBuildMounted = false
    return false
  }
  host.set({
    ...page,
    tag: 'div',
    id: null,
    metadata: null,
    gridRow: null,
    gridColumn: null,
    height: '100%',
    minHeight: '0',
    style: { ...(page.style || {}), background: 'transparent' },
    scope: { ...(page.scope || {}), addAppThreadInUrl: false }
  })
  // The lazy page can settle after the initial view measurement. Read the
  // mounted layout again so a fresh sign-in does not need a window resize.
  this.call('homeBuildFit', host)
  return true
}

// A pane over the home holds /add-app (either pane; a deep link's query does
// not matter). Every key is read up front (reactive callers).
export const homeBuildInPane = function homeBuildInPane(root) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this.context && this.context.functions) || {}
  const panes = [r._canvasSlot, r._canvasSlotSecond]
  const bare = (p) => {
    const raw = String(p).split(/[?#]/)[0]
    const s =
      typeof fns.stripWorkspacePrefix === 'function' ? this.call('stripWorkspacePrefix', raw) : raw
    return (typeof s === 'string' ? s : raw).replace(/\/+$/, '')
  }
  return panes.some((p) => typeof p === 'string' && p !== '' && bare(p) === '/add-app')
}

// The page out of the view: the skeleton is the content again (setting it
// removes the page — its render host gives the address back,
// pages/add-app.js AppRender), and the next show mounts the page anew.
export const homeBuildUnmount = function homeBuildUnmount(el) {
  const host = el || this
  if (!host || !host.__homeBuildMounted || typeof host.set !== 'function') return false
  host.__homeBuildMounted = false
  host.__homeBuildSeq = (Number(host.__homeBuildSeq) || 0) + 1
  host.set({ extends: 'PageSkeleton' })
  return true
}

// The view's top, as it stands with the home scrolled to its start (the home
// scrolls in MainColumn), into `--home-build-top`: the view's height is the
// viewport below it (components/HomeBuildView.js). Only while it is open — a
// closed view lies out of the flow where the dashboard's cards start — and
// only with a box to read; the next show reads it.
export const homeBuildFit = function homeBuildFit(el) {
  const host = el || this
  const node = host && host.node
  if (!node || typeof node.getBoundingClientRect !== 'function') return false
  const state = host.state && host.state.root
  if (!this.call('homeBuildShown', state)) return false
  if (typeof node.getClientRects === 'function' && node.getClientRects().length === 0) return false
  if (typeof host.setNodeStyles !== 'function') return false
  const root = typeof host.getRoot === 'function' ? host.getRoot() : null
  const column = root && typeof root.lookdown === 'function' ? root.lookdown('MainColumn') : null
  const scrolled = column && column.node ? Number(column.node.scrollTop) || 0 : 0
  const top = Math.max(0, Math.round(node.getBoundingClientRect().top + scrolled))
  if (host.__homeBuildTop === top) return false
  host.__homeBuildTop = top
  host.setNodeStyles({ '--home-build-top': top + 'px' })
  return true
}

// A pointer resting on the Build tab, or focus reaching it, starts the
// builder's chunk load (functions/slotPrefetch.js prefetchSlotRoute — once per
// shell, the same route chain the PageSlot mounts from); every other tab asks
// nothing. The view mounts from the module cache then.
export const homeBuildPrefetch = function homeBuildPrefetch(value) {
  if (value !== 'build') return false
  const fns = (this.context && this.context.functions) || {}
  if (typeof fns.prefetchSlotRoute !== 'function') return false
  return !!this.call('prefetchSlotRoute', '/add-app')
}
